/**
 * Central registry for GTM click identifiers.
 *
 * Every business CTA tracked through GTM must use a value from this file.
 *
 * ## Naming convention
 *
 * DOM values follow `<module>-<feature>-<action>` (kebab-case):
 *
 * - `flight-search-lets-go` — module `flight-search`, action `lets-go`
 * - `flight-results-select` — module `flight-results`, action `select`
 * - `booking-traveller-continue` — module `booking`, feature `traveller`, action `continue`
 *
 * Cross-cutting features may use a short feature prefix (e.g. `flight-hmc-chip`, `flight-insights-cheapest`).
 *
 * ## Rules
 *
 * - Never hardcode `data-gtm` strings in components — use `createGtmAttributes()`.
 * - Add new identifiers here before using them in JSX.
 * - Keep identifiers stable once released to production.
 * - GTM trigger names and GA4 event names belong in the GTM container — not in React.
 *
 * @see README.md
 * @see GTM_CLICK_NAMING.md
 * @see GTM_TRACKING_MATRIX.md
 * @see ANALYTICS_CHANGELOG.md
 * @module analytics/gtmClickIds
 */

/**
 * HTML attribute name for GTM click tracking.
 * Set on the element that receives the user click.
 * Prefer `createGtmAttributes(clickId)` over spreading this key manually.
 *
 * @type {string}
 * @constant
 */
export const GTM_DATA_ATTR = 'data-gtm';

/**
 * Supplementary attribute names for optional dynamic context on the same element.
 * Pair with `GTM_CLICK_IDS` when one click id covers many instances.
 *
 * **Approved attributes (frozen):**
 *
 * | Constant | DOM attribute | When to use |
 * |----------|---------------|-------------|
 * | `CONTEXT` | `data-gtm-context` | Default for dynamic context (chip keys, viewport, etc.) |
 * | `CHIP` | `data-gtm-chip` | Help Me Choose chip ids — stable semantic vocabulary |
 * | `FARE` | `data-gtm-fare` | Special fare keys — one click id, many fare pills |
 * | `RESULT_INDEX` | `data-flight-id` | Pre-existing DOM convention for TBO `resultIndex` |
 *
 * Prefer `CONTEXT` unless the paired click id has a well-established domain vocabulary
 * (as with HMC chips and special fares).
 *
 * @type {Readonly<{ FARE: string, CHIP: string, CONTEXT: string, RESULT_INDEX: string }>}
 * @constant
 *
 * @example
 * // Special fare pill
 * { [GTM_DATA_ATTR]: GTM_CLICK_IDS.FLIGHT_SEARCH_SPECIAL_FARE, [GTM_CONTEXT_ATTRS.FARE]: 'student' }
 *
 * @example
 * // Help Me Choose chip
 * { [GTM_DATA_ATTR]: GTM_CLICK_IDS.FLIGHT_HMC_CHIP, [GTM_CONTEXT_ATTRS.CHIP]: 'best-overall' }
 *
 * @example
 * // Mobile filter chip — generic context carries the chip key
 * { [GTM_DATA_ATTR]: GTM_CLICK_IDS.FLIGHT_RESULTS_FILTER_CHIP, [GTM_CONTEXT_ATTRS.CONTEXT]: 'stop-0' }
 */
export const GTM_CONTEXT_ATTRS = {
  /** Special fare key — e.g. `regular`, `student`, `armedForces`, `seniorCitizen` */
  FARE: 'data-gtm-fare',
  /** Help Me Choose chip id — e.g. `best-overall`, `cheap-sensible` */
  CHIP: 'data-gtm-chip',
  /**
   * Generic contextual value for the click — chip key, viewport, etc.
   * Pair with the appropriate `click_id` so GTM can interpret the value.
   */
  CONTEXT: 'data-gtm-context',
  /**
   * DOM attribute holding the TBO `resultIndex` on flight cards and Select CTAs.
   * Session-scoped search result position — not a persistent flight identifier.
   * Pair with `traceId` / `sessionId` server-side for a unique result reference.
   */
  RESULT_INDEX: 'data-flight-id',
};

/**
 * All GTM click identifiers for the flight booking funnel (v1 — frozen).
 *
 * - **Keys:** `SCREAMING_SNAKE_CASE` — use as `GTM_CLICK_IDS.FLIGHT_SEARCH_LETS_GO`
 * - **Values:** kebab-case strings written to the DOM as `data-gtm`
 *
 * @type {Readonly<Record<string, string>>}
 * @constant
 */
export const GTM_CLICK_IDS = {
  // Flight Search (Home — FlightForm)
  FLIGHT_SEARCH_LETS_GO: 'flight-search-lets-go',
  FLIGHT_SEARCH_TRIP_ONEWAY: 'flight-search-trip-oneway',
  FLIGHT_SEARCH_TRIP_ROUNDTRIP: 'flight-search-trip-roundtrip',
  FLIGHT_SEARCH_TRIP_MULTICITY: 'flight-search-trip-multicity',
  FLIGHT_SEARCH_SPECIAL_FARE: 'flight-search-special-fare',

  // Flight Results (hero, sort, filters, cards)
  FLIGHT_RESULTS_LETS_GO: 'flight-results-lets-go',
  FLIGHT_RESULTS_SELECT: 'flight-results-select',
  FLIGHT_RESULTS_VIEW_MORE: 'flight-results-view-more',
  FLIGHT_RESULTS_SORT_FASTEST: 'flight-results-sort-fastest',
  FLIGHT_RESULTS_SORT_CHEAPEST: 'flight-results-sort-cheapest',
  FLIGHT_RESULTS_FILTER_APPLY: 'flight-results-filter-apply',
  /** Clears one filter section (e.g. stops, departure, refundability). */
  FLIGHT_RESULTS_FILTER_CLEAR: 'flight-results-filter-clear',
  /** Clears all active filters across every section. */
  FLIGHT_RESULTS_FILTER_CLEAR_ALL: 'flight-results-filter-clear-all',
  FLIGHT_RESULTS_FILTER_RESET: 'flight-results-filter-reset',
  FLIGHT_RESULTS_FILTER_CHIP: 'flight-results-filter-chip',

  // Modify Search (FlightSelectHeader)
  FLIGHT_MODIFY_SEARCH_OPEN: 'flight-modify-search-open',
  FLIGHT_MODIFY_SEARCH_SUBMIT: 'flight-modify-search-submit',

  // Help Me Choose
  FLIGHT_HMC_PROMPT_SUBMIT: 'flight-hmc-prompt-submit',
  FLIGHT_HMC_CHIP: 'flight-hmc-chip',
  FLIGHT_HMC_VIEW_ITINERARY: 'flight-hmc-view-itinerary',

  // Flight Insights
  FLIGHT_INSIGHTS_CHEAPEST: 'flight-insights-cheapest',
  FLIGHT_INSIGHTS_FASTEST: 'flight-insights-fastest',

  // Flight Selection (sticky bar + fare modal)
  FLIGHT_SELECTION_PROCEED: 'flight-selection-proceed',
  FLIGHT_FARE_SELECT: 'flight-fare-select',
  FLIGHT_FARE_CONTINUE: 'flight-fare-continue',

  // Booking — Traveller details & fare information
  BOOKING_CONTINUE: 'booking-traveller-continue',
  BOOKING_TRAVELLER_ADD: 'booking-traveller-add',
  BOOKING_TRAVELLER_REMOVE: 'booking-traveller-remove',
  BOOKING_FARE_RULES: 'booking-fare-rules',
  BOOKING_BAGGAGE: 'booking-baggage',
  BOOKING_CANCELLATION_POLICY: 'booking-cancellation-policy',

  // Review
  REVIEW_CONFIRM: 'review-confirm',
  REVIEW_EDIT: 'review-edit',

  // SSR
  SSR_SKIP_PAYMENT: 'ssr-skip-payment',
  SSR_CONTINUE_PAYMENT: 'ssr-continue-payment',

  // Preview
  PREVIEW_CONTINUE: 'preview-continue',

  // Hotels — Search (Home — HotelForm, inline search bar)
  HOTEL_SEARCH_LETS_GO: 'hotel-search-lets-go',
  HOTEL_SEARCH_AUTOCOMPLETE_SELECT: 'hotel-search-autocomplete-select',
  HOTEL_SEARCH_NEARBY_LOCATION: 'hotel-search-nearby-location',

  // Hotels — Results (filters, sort, cards, map)
  HOTEL_RESULTS_FILTER: 'hotel-results-filter',
  HOTEL_RESULTS_SORT: 'hotel-results-sort',
  HOTEL_RESULTS_VIEW_ROOMS: 'hotel-results-view-rooms',

  // Hotels — Detail (tabs, TripAdvisor, select room)
  HOTEL_DETAIL_TRIPADVISOR_REVIEWS: 'hotel-detail-tripadvisor-reviews',
  HOTEL_DETAIL_TAB: 'hotel-detail-tab',
  HOTEL_DETAIL_SELECT_ROOM: 'hotel-detail-select-room',
  HOTEL_DETAIL_REVIEW_PHOTOS: 'hotel-detail-review-photos',

  // Hotels — Guest details (booking page)
  HOTEL_BOOKING_HOTEL_RULES: 'hotel-booking-hotel-rules',
  HOTEL_BOOKING_CANCELLATION_POLICY: 'hotel-booking-cancellation-policy',
  HOTEL_BOOKING_VIEW_POLICY: 'hotel-booking-view-policy',
  HOTEL_GUEST_FORM_OPEN: 'hotel-guest-form-open',
  HOTEL_GUEST_FORM_CLOSE: 'hotel-guest-form-close',
  HOTEL_GUEST_CHOOSE_SAVED: 'hotel-guest-choose-saved',
  HOTEL_GUEST_CONTINUE: 'hotel-guest-continue',

  // Hotels — Review
  HOTEL_REVIEW_CONTINUE_PAYMENT: 'hotel-review-continue-payment',
};

/**
 * Creates GTM data attributes for a clickable element.
 *
 * Always spread onto the element that owns the `onClick` handler.
 * Never assign `[GTM_DATA_ATTR]` manually in components.
 *
 * @param {string} clickId - Value from `GTM_CLICK_IDS` (written to `data-gtm`).
 * @param {Object.<string, string>} [extraAttrs={}] - Optional supplementary attributes
 *   using keys from `GTM_CONTEXT_ATTRS` or pre-existing DOM attrs like `data-flight-id`.
 * @returns {Object.<string, string>} Props to spread onto the clickable element.
 *
 * @example
 * // Simple click
 * <Button {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_LETS_GO)} />
 *
 * @example
 * // Click with domain context
 * <Button
 *   {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_SPECIAL_FARE, {
 *     [GTM_CONTEXT_ATTRS.FARE]: fareKey,
 *   })}
 * />
 *
 * @example
 * // Click with generic context
 * <button
 *   {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_RESULTS_FILTER_CHIP, {
 *     [GTM_CONTEXT_ATTRS.CONTEXT]: chip.key,
 *   })}
 * />
 */
export function createGtmAttributes(clickId, extraAttrs = {}) {
  return {
    [GTM_DATA_ATTR]: clickId,
    ...extraAttrs,
  };
}
